import axiosInstance from './axiosInstance';
import { offlineQueue, QueueItem, MAX_RETRY } from './offlineQueueService';
import { loadPhotoAsFile, deleteQueuePhoto } from './offlineFileService';

// Guard agar tidak ada 2 sync berjalan bersamaan
let _isSyncing = false;

export async function runSync(onProgress?: (pending: number) => void, currentUserId?: string): Promise<void> {
  if (_isSyncing) return;
  _isSyncing = true;

  try {
    const items = await offlineQueue.getPending();
    if (!items.length) return;

    for (const item of items) {
      // Lewati item milik user lain (terjadi saat re-login dengan akun berbeda)
      // Gunakan String() agar perbandingan tidak gagal karena perbedaan tipe (number vs string)
      if (currentUserId && String(item.user_id) !== String(currentUserId)) {
        await offlineQueue.markFailed(item.id, 'User mismatch — antrian dibuat oleh akun yang berbeda');
        onProgress?.(await offlineQueue.getPendingCount());
        continue;
      }

      // Lewati item yang sudah mencapai batas retry — perlu diekspor ke admin
      if (item.retry_count >= MAX_RETRY) continue;

      await offlineQueue.markSyncing(item.id);
      try {
        await processItem(item);
        await offlineQueue.markDone(item.id);
        await cleanupItemPhotos(item);
      } catch (err: any) {
        // 400 dengan pesan "already" = data sudah masuk sebelumnya (idempotent) → anggap selesai
        const msg: string = err?.response?.data?.message || err?.message || 'Unknown error';
        const isAlreadyDone = err?.response?.status === 400 && (
          msg.toLowerCase().includes('already') ||
          msg.toLowerCase().includes('sudah')
        );
        if (isAlreadyDone) {
          await offlineQueue.markDone(item.id);
          await cleanupItemPhotos(item);
        } else {
          // Deteksi konflik status: backend menolak karena status task sudah berubah oleh pengguna lain
          const isStatusConflict = err?.response?.status === 400 && (
            msg.toLowerCase().includes('only in progress tasks can be marked as pending') ||
            msg.toLowerCase().includes('not eligible to start') ||
            msg.toLowerCase().includes('task is not eligible') ||
            msg.toLowerCase().includes('status not eligible') ||
            err?.response?.status === 409
          );
          const finalMsg = isStatusConflict
            ? `Konflik status: ${msg} — status task mungkin sudah diubah pengguna lain saat Anda offline.`
            : msg;
          await offlineQueue.markFailed(item.id, finalMsg);
        }
      }
      // Update pending count setelah setiap item
      const remaining = await offlineQueue.getPendingCount();
      onProgress?.(remaining);
    }

    await offlineQueue.clearDone();
  } finally {
    _isSyncing = false;
  }
}

async function buildAttendanceFormData(payload: Record<string, any>): Promise<FormData> {
  const fd = new FormData();
  fd.append('latitude', String(payload.latitude));
  fd.append('longitude', String(payload.longitude));
  fd.append('address', payload.address || '');
  fd.append('is_business_trip', String(payload.is_business_trip ?? false));
  if (payload.note) fd.append('note', payload.note);
  if (payload.gps_accuracy != null) fd.append('gps_accuracy', String(payload.gps_accuracy));
  fd.append('is_mock_location', String(payload.is_mock_location === true));
  const photoFile = await loadPhotoAsFile(payload.photo_ref);
  fd.append('photo', photoFile);
  return fd;
}

async function processItem(item: QueueItem): Promise<void> {
  const headers = {
    'Content-Type': 'multipart/form-data',
    'X-Idempotency-Key': item.id,
    'X-Client-Timestamp': item.client_timestamp,
  };

  switch (item.action_type) {
    case 'attendance_checkin': {
      const fd = await buildAttendanceFormData(item.payload);
      if (item.payload.wifi_ssid) fd.append('wifi_ssid', item.payload.wifi_ssid);
      if (item.payload.wifi_bssid) fd.append('wifi_bssid', item.payload.wifi_bssid);
      await axiosInstance.post('/attendances/check-in', fd, { headers });
      break;
    }
    case 'attendance_break_out': {
      const fd = await buildAttendanceFormData(item.payload);
      await axiosInstance.put('/attendances/break-out', fd, { headers });
      break;
    }
    case 'attendance_break_in': {
      const fd = await buildAttendanceFormData(item.payload);
      await axiosInstance.put('/attendances/break-in', fd, { headers });
      break;
    }
    case 'attendance_checkout': {
      const fd = await buildAttendanceFormData(item.payload);
      if (item.payload.wifi_ssid) fd.append('wifi_ssid', item.payload.wifi_ssid);
      if (item.payload.wifi_bssid) fd.append('wifi_bssid', item.payload.wifi_bssid);
      await axiosInstance.put('/attendances/check-out', fd, { headers });
      break;
    }
    case 'task_pending': {
      const { task_id, notes } = item.payload;
      await axiosInstance.post(`/tasks/${task_id}/pending`, { notes: notes || null }, {
        headers: { 'X-Idempotency-Key': item.id, 'X-Client-Timestamp': item.client_timestamp },
      });
      break;
    }
    case 'task_start': {
      const { task_id } = item.payload;
      await axiosInstance.post(`/tasks/${task_id}/start`, {}, {
        headers: { 'X-Idempotency-Key': item.id, 'X-Client-Timestamp': item.client_timestamp },
      });
      break;
    }
    case 'feedback_comment': {
      const { feedback_id, feedback_attachment_id, comment, photo_ref } = item.payload;
      const fd = new FormData();
      if (comment) fd.append('comment', comment);
      if (feedback_attachment_id) fd.append('feedback_attachment_id', String(feedback_attachment_id));
      if (photo_ref) {
        const file = await loadPhotoAsFile(photo_ref);
        fd.append('attachment', file);
      }
      await axiosInstance.post(`/tasks/feedbacks/${feedback_id}/comments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Idempotency-Key': item.id },
      });
      break;
    }
    case 'task_report_submit': {
      const { task_id, description, latitude, longitude, address,
              attachments, additional_files, text_attachments,
              retained_attachment_ids, retained_attachment_updates } = item.payload;

      const fd = new FormData();
      fd.append('description', description);
      fd.append('latitude', String(latitude));
      fd.append('longitude', String(longitude));
      fd.append('address', address || '');

      // Retained draft attachments
      if (retained_attachment_ids?.length) {
        retained_attachment_ids.forEach((id: number) => fd.append('retained_attachment_ids', String(id)));
        fd.append('retained_attachment_updates', JSON.stringify(retained_attachment_updates || []));
      } else {
        fd.append('retained_attachment_ids', '');
      }

      // File attachments — muat dari offline storage
      for (const att of (attachments || [])) {
        const file = await loadPhotoAsFile(att.photo_ref);
        fd.append('attachments', file);
        fd.append('task_checklist_ids', att.task_checklist_id || '');
        fd.append('attachment_notes', att.note || '');
        fd.append('attachment_timings', att.timing || '');
      }

      // Additional files
      for (const att of (additional_files || [])) {
        const file = await loadPhotoAsFile(att.photo_ref);
        fd.append('attachments', file);
        fd.append('task_checklist_ids', '');
        fd.append('attachment_notes', att.note || '');
        fd.append('attachment_timings', '');
      }

      // Text attachments
      if (text_attachments?.length) {
        fd.append('text_attachments', JSON.stringify(text_attachments));
      }

      await axiosInstance.post(`/tasks/${task_id}/submit-feedback`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Idempotency-Key': item.id },
      });
      break;
    }
  }
}

async function cleanupItemPhotos(item: QueueItem): Promise<void> {
  const { payload } = item;
  try {
    if (payload.photo_ref) await deleteQueuePhoto(payload.photo_ref);
    for (const att of payload.attachments || []) {
      if (att.photo_ref) await deleteQueuePhoto(att.photo_ref);
    }
    for (const att of payload.additional_files || []) {
      if (att.photo_ref) await deleteQueuePhoto(att.photo_ref);
    }
    // feedback_comment: photo_ref sudah dicakup oleh baris pertama
  } catch {
    // Cleanup failure tidak boleh blokir sync
  }
}
